import { memo, useCallback, useContext, useMemo, useEffect } from 'react'

import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR } from 'constants/offer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BundledHotelPriceContent from './BundledHotelPriceContent'
import HotelPriceContent from './HotelPriceContent'
import type { DistanceUnit } from 'lib/geo/distanceUnits'
import { getDistanceLabel } from 'lib/customer/recommendationUtils'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable/Clickable'
import { useInView } from 'react-intersection-observer'
import AspectRatio from 'components/utils/AspectRatio'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { OfferBestPriceData } from 'selectors/offerSelectors'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isHotelPackage, isLEHotel } from 'lib/offer/offerTypes'
import GeoContext from 'contexts/geoContext'
import { useIntl } from 'react-intl'

const Root = styled(Clickable)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;
  transition: outline 0.2s, box-shadow 0.2s;

  ${mediaQueryUp.tablet} {
    padding: ${rem(8)};
    width: unset;
    min-width: ${rem(380)};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      }
    }
  }

  &.tile-border {
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.XS};
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const validOfferTypes: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_TOUR,
  'direct_tour',
  'partner_tour',
  'connection_tour',
  OFFER_TYPE_BED_BANK,
]

export function shouldDisplay(offerType: App.OfferType) {
  return validOfferTypes.includes(offerType)
}

interface PricingProps {
  duration: string
  hotelNights?: number
  price: number
  memberPrice: number
  saleUnit: string
}

interface FlightProps {
  bundledWithFlightsOnly?: boolean
  flightOrigin?: string
  flightPrices?: { [airportCode: string]: number }
}

interface Props {
  image: App.Image
  title: string
  location: string
  offerType: App.OfferType
  position: number
  testId: string
  url: string
  distance?: number
  distanceFrom?: string
  distanceUnit?: DistanceUnit
  flights?: FlightProps
  shouldDisplayValue?: boolean
  value?: number
  rating?: App.OfferRating
  pricing?: PricingProps
  offer?: App.AnyOffer
  alternativePrice?: OfferBestPriceData
  onTileClick?: () => void
}

function YouMayAlsoLikeTile({
  distance,
  distanceFrom,
  distanceUnit,
  flights,
  image,
  title,
  offerType,
  position,
  pricing,
  shouldDisplayValue,
  url,
  value,
  rating,
  testId,
  offer,
  alternativePrice,
  onTileClick,
}: Props) {
  const intl = useIntl()
  const useAlternativeData = !!alternativePrice && alternativePrice.bestPrice

  let price: number
  let memberPrice: number
  let hotelNights: number | undefined
  let duration: string | undefined

  if (useAlternativeData && alternativePrice) {
    price = alternativePrice.bestPrice
    memberPrice = alternativePrice.bestMemberPrice ?? 0
    hotelNights = alternativePrice.duration

    // Bedbanks dont have a duration label sometimes so we need to construct it
    duration = alternativePrice.bestPricePackage?.durationLabel
      ?? (typeof alternativePrice.duration === 'number'
        ? intl.formatMessage(
            {
              id: 'X5Ed2NDT6VFRBITGjuyz',
              defaultMessage: '{count, plural, one {# night} other {# nights}}',
              description: 'Night count duration label for you may also like tile',
            },
            { count: alternativePrice.duration },
          )
        : undefined)
  } else {
    price = pricing?.price ?? 0
    memberPrice = pricing?.memberPrice ?? 0
    hotelNights = pricing?.hotelNights
    duration = pricing?.duration
  }

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    skip: !offer,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (inView && !!offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position,
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, offer, position])

  const fireProductClick = useCallback(() => {
    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer,
        position,
      })
    }
  }, [dispatchOfferListEvent, offer, position])

  const handleClick = useCallback(() => {
    fireProductClick()
    if (onTileClick) {
      onTileClick()
    }
  }, [fireProductClick, onTileClick])

  const showHotelPrice = pricing && !flights?.bundledWithFlightsOnly
  const showBundledHotelPrice = pricing && flights?.bundledWithFlightsOnly

  // Narrow to LE Hotel to safely access hotel package fields
  const hotelOffer: App.HotelOffer | App.HotelOfferSummary | undefined = useMemo(() => {
    if (offer && isLEHotel(offer)) {
      return offer
    }
    return undefined
  }, [offer])

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyPointsRequest = useMemo<App.LoyaltyPointsEarnCalculationRequest | undefined>(() => {
    const pkgCandidate = alternativePrice?.bestPricePackage ?? hotelOffer?.lowestPricePackage
    const pkg = isHotelPackage(pkgCandidate) ? pkgCandidate : undefined

    return generateLoyaltyPointsCalculatorHotelOptions(hotelOffer, {
      pkg,
      price: pkg?.price,
      memberPrice: pkg?.memberPrice,
      currentRegionCode,
      nights: pkg?.duration,
    })
  }, [hotelOffer, alternativePrice, currentRegionCode])

  const distanceText = useMemo(() => {
    if (!distance || !distanceFrom || !distanceUnit) {
      return null
    }
    return getDistanceLabel(distance, distanceFrom, distanceUnit, intl)
  }, [distance, distanceUnit, distanceFrom, intl])

  if (!shouldDisplay(offerType)) {
    return null
  }

  return (
    <Root
      to={url}
      onClick={handleClick}
      target="_blank"
      data-testid={testId}
      ref={inViewRef}
    >
      <Group direction="horizontal" gap={12}>
        <AspectRatio ratio="5:6" width="100px">
          <Image
            id={image.id}
            alt={image.title}
            width={100}
            height={120}
            dpr={2}
            fit="center"
          />
        </AspectRatio>
        <Group direction="vertical" fullWidth gap={8}>
          <Group direction="vertical" fullHeight gap={4}>
            <Group direction="vertical">
              {distanceText && <Caption variant="large" colour="light-foreground-alt" lineClamp={1} wrap="letter-wrap">{distanceText}</Caption>}
              <BodyText variant="medium" weight="bold" lineClamp={1} wrap="letter-wrap">{title}</BodyText>
            </Group>
            {isOfferRatingDisplayable(rating) && <OfferRating variant="xs" rating={rating} hideLink />}
          </Group>
          {showHotelPrice &&
            <HotelPriceContent
              offerType={offerType}
              price={price}
              memberPrice={memberPrice}
              duration={duration}
              value={value}
              hotelNights={hotelNights}
              shouldDisplayValue={shouldDisplayValue}
            />}
          {showBundledHotelPrice &&
            <BundledHotelPriceContent
              bundledWithFlightsOnly={!!flights?.bundledWithFlightsOnly}
              flightPrices={flights?.flightPrices ?? {}}
              price={price}
              memberPrice={memberPrice}
              duration={duration!}
              value={value}
              offer={offer}
            />}
          <LoyaltyPointsEarn hidePromoPill hideInfoIcon calculationRequests={loyaltyPointsRequest} calculationType="estimate" offer={hotelOffer} />
        </Group>
      </Group>
    </Root>
  )
}

export default memo(YouMayAlsoLikeTile)
